import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Mail,
  Plus,
  Search,
  Users,
  FileText,
  Send,
  BarChart3,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  ListFilter,
  Sparkles,
  Tag,
  Calendar,
  MousePointerClick,
  MailOpen,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from "lucide-react";
import EmailBuilder from "../components/EmailBuilder";

import { API_BASE_URL as API } from "../lib/backendUrl";
import { classifyWorkflowError } from "../lib/workflowErrors";

const EmailMarketingPage = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Dialog states
  const [isListDialogOpen, setIsListDialogOpen] = useState(false);
  const [isTemplateBuilderOpen, setIsTemplateBuilderOpen] = useState(false);
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [listForm, setListForm] = useState({
    name: "",
    description: "",
    list_type: "manual",
    tags: [],
    filter_rules: {},
  });
  
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    description: "",
    list_id: "",
    template_id: "",
    subject_override: "",
    scheduled_at: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, listsRes, templatesRes, campaignsRes, tagsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/email/stats`),
        axios.get(`${API}/email/lists`),
        axios.get(`${API}/email/templates`),
        axios.get(`${API}/email/campaigns`),
        axios.get(`${API}/email/tags`),
        axios.get(`${API}/email/analytics/overview?days=30`),
      ]);
      setStats(statsRes.data);
      setLists(listsRes.data);
      setTemplates(templatesRes.data);
      setCampaigns(campaignsRes.data);
      setTags(tagsRes.data);
      setAnalyticsOverview(analyticsRes.data);
    } catch (error) {
      toast.error("Failed to load email marketing data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/email/analytics/campaign/${campaignId}`);
      setCampaignAnalytics(res.data);
    } catch (error) {
      toast.error("Failed to load campaign analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleViewCampaignAnalytics = (campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignAnalytics(campaign.id);
  };

  // List handlers
  const handleCreateList = async () => {
    if (!listForm.name) {
      toast.error("List name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...listForm,
        tags: listForm.list_type === "manual" ? listForm.tags : null,
        filter_rules: listForm.list_type === "smart" ? listForm.filter_rules : null,
      };
      
      if (editingItem) {
        await axios.put(`${API}/email/lists/${editingItem.id}`, payload);
        toast.success("List updated");
      } else {
        await axios.post(`${API}/email/lists`, payload);
        toast.success("List created");
      }
      setIsListDialogOpen(false);
      resetListForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save list");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteList = async (list) => {
    if (!confirm(`Archive list "${list.name}"?`)) return;
    try {
      await axios.delete(`${API}/email/lists/${list.id}`);
      toast.success("List archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive list");
    }
  };

  const resetListForm = () => {
    setListForm({
      name: "",
      description: "",
      list_type: "manual",
      tags: [],
      filter_rules: {},
    });
    setEditingItem(null);
  };

  // Template handlers - using drag-and-drop builder
  const handleSaveTemplate = async (templateData) => {
    setSubmitting(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/email/templates/${editingTemplate.id}`, templateData);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/email/templates`, templateData);
        toast.success("Template created");
      }
      setIsTemplateBuilderOpen(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Archive template "${template.name}"?`)) return;
    try {
      await axios.delete(`${API}/email/templates/${template.id}`);
      toast.success("Template archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive template");
    }
  };

  // Campaign handlers
  const handleCreateCampaign = async () => {
    if (!campaignForm.name) {
      toast.error("Campaign name is required");
      return;
    }
    setSubmitting(true);
    try {
      // Prepare data with scheduled_at
      const payload = {
        ...campaignForm,
        scheduled_at: campaignForm.scheduled_at || null,
      };
      
      if (editingItem) {
        await axios.put(`${API}/email/campaigns/${editingItem.id}`, payload);
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/email/campaigns`, payload);
        toast.success("Campaign created");
      }
      setIsCampaignDialogOpen(false);
      resetCampaignForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!confirm(`Cancel campaign "${campaign.name}"?`)) return;
    try {
      await axios.delete(`${API}/email/campaigns/${campaign.id}`);
      toast.success("Campaign cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel campaign");
    }
  };

  // Send handlers
  const handleSendTestEmail = async (template) => {
    const email = prompt("Enter email address to send test to:", "");
    if (!email) return;
    
    try {
      await axios.post(`${API}/email/test-email`, {
        template_id: template.id,
        to_email: email,
      });
      toast.success(`Test email sent to ${email}`);
    } catch (error) {
      // The two frozen outbound doors answer 503 {code:"not_enabled",
      // message} -- an OBJECT detail -- and handing that straight to
      // toast.error renders "[object Object]". An operator then reads a
      // deliberate, correct refusal as a broken frontend. Routed through the
      // shared classifier, which already flattens an object detail to its
      // message and knows 503/not_enabled as `disabled`. Scoped to the two
      // send doors on purpose: the same raw-detail pattern is app-wide debt
      // (~100 sites) and is NOT this slice's business.
      toast.error(classifyWorkflowError(error).message || "Failed to send test email");
    }
  };

  const handleSendCampaign = async (campaign) => {
    if (!campaign.list_id) {
      toast.error("Please select a recipient list first");
      return;
    }
    if (!campaign.template_id) {
      toast.error("Please select an email template first");
      return;
    }
    if (!confirm(`Send campaign "${campaign.name}" to all list members? This cannot be undone.`)) return;
    
    try {
      const res = await axios.post(`${API}/email/campaigns/${campaign.id}/send`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      // The two frozen outbound doors answer 503 {code:"not_enabled",
      // message} -- an OBJECT detail -- and handing that straight to
      // toast.error renders "[object Object]". An operator then reads a
      // deliberate, correct refusal as a broken frontend. Routed through the
      // shared classifier, which already flattens an object detail to its
      // message and knows 503/not_enabled as `disabled`. Scoped to the two
      // send doors on purpose: the same raw-detail pattern is app-wide debt
      // (~100 sites) and is NOT this slice's business.
      toast.error(classifyWorkflowError(error).message || "Failed to send campaign");
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: "",
      description: "",
      list_id: "",
      template_id: "",
      subject_override: "",
      scheduled_at: "",
    });
    setEditingItem(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Email Marketing
          </h1>
          <p className="text-muted-foreground">
            Create lists, design templates, and send campaigns
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="tab-analytics">
            <Activity className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="lists" className="flex items-center gap-2" data-testid="tab-lists">
            <Users className="h-4 w-4" />
            Lists
            <Badge variant="secondary" className="ml-1">{lists.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2" data-testid="tab-templates">
            <FileText className="h-4 w-4" />
            Templates
            <Badge variant="secondary" className="ml-1">{templates.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="flex items-center gap-2" data-testid="tab-campaigns">
            <Send className="h-4 w-4" />
            Campaigns
            <Badge variant="secondary" className="ml-1">{campaigns.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6" data-testid="overview-content">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="stat-lists">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Lists</p>
                    <p className="text-3xl font-bold">{stats.total_lists}</p>
                  </div>
                  <Users className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-templates">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Templates</p>
                    <p className="text-3xl font-bold">{stats.total_templates}</p>
                  </div>
                  <FileText className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-campaigns-sent">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Campaigns Sent</p>
                    <p className="text-3xl font-bold">{stats.sent_campaigns}</p>
                  </div>
                  <Send className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-emails-sent">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Emails Sent</p>
                    <p className="text-3xl font-bold">{stats.total_emails_sent}</p>
                  </div>
                  <Mail className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="stat-open-rate">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MailOpen className="h-5 w-5" />
                  Average Open Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-3">
                  <p className="text-4xl font-bold text-primary">{stats.avg_open_rate || 0}%</p>
                  {stats.avg_open_rate > 21.5 ? (
                    <span className="text-green-500 flex items-center text-sm">
                      <ArrowUpRight className="h-4 w-4" />
                      Above avg
                    </span>
                  ) : (
                    <span className="text-yellow-500 flex items-center text-sm">
                      <ArrowDownRight className="h-4 w-4" />
                      Below avg
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Industry average: 21.5%
                </p>
              </CardContent>
            </Card>
            <Card data-testid="stat-click-rate">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MousePointerClick className="h-5 w-5" />
                  Average Click Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-3">
                  <p className="text-4xl font-bold text-primary">{stats.avg_click_rate || 0}%</p>
                  {stats.avg_click_rate > 2.3 ? (
                    <span className="text-green-500 flex items-center text-sm">
                      <ArrowUpRight className="h-4 w-4" />
                      Above avg
                    </span>
                  ) : (
                    <span className="text-yellow-500 flex items-center text-sm">
                      <ArrowDownRight className="h-4 w-4" />
                      Below avg
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Industry average: 2.3%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => { resetListForm(); setIsListDialogOpen(true); }} data-testid="btn-create-list">
                <Plus className="mr-2 h-4 w-4" />
                Create List
              </Button>
              <Button variant="outline" onClick={() => { setEditingTemplate(null); setIsTemplateBuilderOpen(true); }} data-testid="btn-create-template">
                <Plus className="mr-2 h-4 w-4" />
                Create Template
              </Button>
              <Button variant="outline" onClick={() => { resetCampaignForm(); setIsCampaignDialogOpen(true); }} data-testid="btn-create-campaign">
                <Plus className="mr-2 h-4 w-4" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6" data-testid="analytics-content">
          {analyticsOverview ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Sent</p>
                    <p className="text-2xl font-bold mt-1">{analyticsOverview.summary.total_sent.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Delivered</p>
                    <p className="text-2xl font-bold mt-1 text-green-500">{analyticsOverview.summary.total_delivered.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Opened</p>
                    <p className="text-2xl font-bold mt-1 text-blue-500">{analyticsOverview.summary.total_opened.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Clicked</p>
                    <p className="text-2xl font-bold mt-1 text-purple-500">{analyticsOverview.summary.total_clicked.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Bounced</p>
                    <p className="text-2xl font-bold mt-1 text-orange-500">{analyticsOverview.summary.total_bounced.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Unsubscribed</p>
                    <p className="text-2xl font-bold mt-1 text-red-500">{analyticsOverview.summary.total_unsubscribed.toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Rate Cards with Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <MailOpen className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-muted-foreground">Open Rate</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">{analyticsOverview.summary.open_rate}%</span>
                      <span className={`text-sm ${analyticsOverview.summary.open_rate >= analyticsOverview.benchmarks.industry_open_rate ? 'text-green-500' : 'text-red-500'}`}>
                        {analyticsOverview.summary.open_rate >= analyticsOverview.benchmarks.industry_open_rate ? (
                          <span className="flex items-center"><TrendingUp className="h-3 w-3 mr-1" />{(analyticsOverview.summary.open_rate - analyticsOverview.benchmarks.industry_open_rate).toFixed(1)}%</span>
                        ) : (
                          <span className="flex items-center"><TrendingDown className="h-3 w-3 mr-1" />{(analyticsOverview.benchmarks.industry_open_rate - analyticsOverview.summary.open_rate).toFixed(1)}%</span>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Industry: {analyticsOverview.benchmarks.industry_open_rate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <MousePointerClick className="h-4 w-4 text-purple-500" />
                      <span className="text-sm text-muted-foreground">Click Rate</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">{analyticsOverview.summary.click_rate}%</span>
                      <span className={`text-sm ${analyticsOverview.summary.click_rate >= analyticsOverview.benchmarks.industry_click_rate ? 'text-green-500' : 'text-red-500'}`}>
                        {analyticsOverview.summary.click_rate >= analyticsOverview.benchmarks.industry_click_rate ? (
                          <span className="flex items-center"><TrendingUp className="h-3 w-3 mr-1" />{(analyticsOverview.summary.click_rate - analyticsOverview.benchmarks.industry_click_rate).toFixed(1)}%</span>
                        ) : (
                          <span className="flex items-center"><TrendingDown className="h-3 w-3 mr-1" />{(analyticsOverview.benchmarks.industry_click_rate - analyticsOverview.summary.click_rate).toFixed(1)}%</span>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Industry: {analyticsOverview.benchmarks.industry_click_rate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-muted-foreground">Bounce Rate</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">{analyticsOverview.summary.bounce_rate}%</span>
                      <span className={`text-sm ${analyticsOverview.summary.bounce_rate <= analyticsOverview.benchmarks.industry_bounce_rate ? 'text-green-500' : 'text-red-500'}`}>
                        {analyticsOverview.summary.bounce_rate <= analyticsOverview.benchmarks.industry_bounce_rate ? (
                          <span className="flex items-center"><TrendingDown className="h-3 w-3 mr-1" />Good</span>
                        ) : (
                          <span className="flex items-center"><TrendingUp className="h-3 w-3 mr-1" />High</span>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Industry: {analyticsOverview.benchmarks.industry_bounce_rate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-muted-foreground">Unsubscribe Rate</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">{analyticsOverview.summary.unsubscribe_rate}%</span>
                      <span className={`text-sm ${analyticsOverview.summary.unsubscribe_rate <= analyticsOverview.benchmarks.industry_unsubscribe_rate ? 'text-green-500' : 'text-red-500'}`}>
                        {analyticsOverview.summary.unsubscribe_rate <= analyticsOverview.benchmarks.industry_unsubscribe_rate ? (
                          <span className="flex items-center"><TrendingDown className="h-3 w-3 mr-1" />Good</span>
                        ) : (
                          <span className="flex items-center"><TrendingUp className="h-3 w-3 mr-1" />High</span>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Industry: {analyticsOverview.benchmarks.industry_unsubscribe_rate}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Campaigns Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Recent Campaign Performance
                  </CardTitle>
                  <CardDescription>Last 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {analyticsOverview.recent_campaigns.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Send className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No campaigns sent in the last 30 days</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Campaign</TableHead>
                            <TableHead>Sent</TableHead>
                            <TableHead>Delivered</TableHead>
                            <TableHead>Opened</TableHead>
                            <TableHead>Clicked</TableHead>
                            <TableHead>Open Rate</TableHead>
                            <TableHead>Click Rate</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analyticsOverview.recent_campaigns.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{c.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '-'}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell>{c.total_sent.toLocaleString()}</TableCell>
                              <TableCell>{c.total_delivered.toLocaleString()}</TableCell>
                              <TableCell>{c.total_opened.toLocaleString()}</TableCell>
                              <TableCell>{c.total_clicked.toLocaleString()}</TableCell>
                              <TableCell>
                                <Badge variant={c.open_rate >= 21.5 ? "default" : "secondary"}>
                                  {c.open_rate}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={c.click_rate >= 2.3 ? "default" : "secondary"}>
                                  {c.click_rate}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleViewCampaignAnalytics({id: c.id, name: c.name})}
                                  data-testid={`view-analytics-${c.id}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          )}
        </TabsContent>

        {/* Lists Tab */}
        <TabsContent value="lists" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Input placeholder="Search lists..." className="w-64" />
            </div>
            <Button onClick={() => { resetListForm(); setIsListDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Create List
            </Button>
          </div>

          {lists.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Users className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No lists yet</h3>
                <p className="text-muted-foreground mb-4">Create your first contact list to start sending campaigns</p>
                <Button onClick={() => { resetListForm(); setIsListDialogOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create List
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lists.map((list) => (
                    <TableRow key={list.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{list.name}</p>
                          {list.description && (
                            <p className="text-sm text-muted-foreground">{list.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          {list.list_type === "smart" ? (
                            <><Sparkles className="h-3 w-3" /> Smart</>
                          ) : (
                            <><Tag className="h-3 w-3" /> Manual</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{list.member_count}</span>
                        <span className="text-muted-foreground ml-1">contacts</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={list.status === "active" ? "default" : "secondary"}>
                          {list.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingItem(list);
                            setListForm({
                              name: list.name,
                              description: list.description || "",
                              list_type: list.list_type,
                              tags: list.tags || [],
                              filter_rules: list.filter_rules || {},
                            });
                            setIsListDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteList(list)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Input placeholder="Search templates..." className="w-64" />
            </div>
            <Button onClick={() => { setEditingTemplate(null); setIsTemplateBuilderOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Create Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-4">Create reusable email templates for your campaigns</p>
                <Button onClick={() => { setEditingTemplate(null); setIsTemplateBuilderOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        {template.category && (
                          <Badge variant="outline" className="mt-1 capitalize">
                            {template.category}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Send Test Email"
                          onClick={() => handleSendTestEmail(template)}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingTemplate(template);
                            setIsTemplateBuilderOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTemplate(template)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">Subject:</p>
                    <p className="text-sm font-medium line-clamp-2">{template.subject}</p>
                    {template.variables && template.variables.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {template.variables.map((v) => (
                          <Badge key={v} variant="secondary" className="text-xs">
                            {`{{${v}}}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Input placeholder="Search campaigns..." className="w-64" />
            </div>
            <Button onClick={() => { resetCampaignForm(); setIsCampaignDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Send className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No campaigns yet</h3>
                <p className="text-muted-foreground mb-4">Create your first email campaign to reach your contacts</p>
                <Button onClick={() => { resetCampaignForm(); setIsCampaignDialogOpen(true); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Campaign
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>List</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Open Rate</TableHead>
                    <TableHead>Click Rate</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          {campaign.template_name && (
                            <p className="text-sm text-muted-foreground">
                              Template: {campaign.template_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {campaign.list_name || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            campaign.status === "sent" ? "default" :
                            campaign.status === "draft" ? "secondary" :
                            campaign.status === "sending" ? "outline" :
                            "destructive"
                          }
                        >
                          {campaign.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{campaign.total_sent}</TableCell>
                      <TableCell>
                        <span className="font-medium">{campaign.open_rate}%</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{campaign.click_rate}%</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {campaign.status === "sent" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="View Analytics"
                            onClick={() => handleViewCampaignAnalytics(campaign)}
                            data-testid={`campaign-analytics-${campaign.id}`}
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        )}
                        {campaign.status === "draft" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Send Campaign"
                            onClick={() => handleSendCampaign(campaign)}
                            className="text-primary hover:text-primary"
                            data-testid={`send-campaign-${campaign.id}`}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingItem(campaign);
                            setCampaignForm({
                              name: campaign.name,
                              description: campaign.description || "",
                              list_id: campaign.list_id || "",
                              template_id: campaign.template_id || "",
                              subject_override: campaign.subject_override || "",
                              scheduled_at: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : "",
                            });
                            setIsCampaignDialogOpen(true);
                          }}
                          disabled={campaign.status === "sent"}
                          data-testid={`edit-campaign-${campaign.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteCampaign(campaign)}
                          disabled={campaign.status === "sent"}
                          data-testid={`delete-campaign-${campaign.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit List Dialog */}
      <Dialog open={isListDialogOpen} onOpenChange={setIsListDialogOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit List" : "Create List"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>List Name *</Label>
              <Input
                value={listForm.name}
                onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
                placeholder="e.g., Newsletter Subscribers"
                className="bg-muted/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={listForm.description}
                onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
                placeholder="What is this list for?"
                className="bg-muted/50"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>List Type</Label>
              <Select
                value={listForm.list_type}
                onValueChange={(v) => setListForm({ ...listForm, list_type: v })}
              >
                <SelectTrigger className="bg-muted/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">
                    <span className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Manual (Tag-based)
                    </span>
                  </SelectItem>
                  <SelectItem value="smart">
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Smart (Filter-based)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {listForm.list_type === "manual" && (
              <div className="space-y-2">
                <Label>Tags</Label>
                <p className="text-sm text-muted-foreground">
                  Contacts with these tags will be included in the list
                </p>
                <Input
                  placeholder="Enter tag names (comma-separated)"
                  className="bg-muted/50"
                  value={listForm.tags.join(", ")}
                  onChange={(e) => setListForm({ 
                    ...listForm, 
                    tags: e.target.value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean)
                  })}
                />
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-xs text-muted-foreground mr-2">Existing tags:</span>
                    {tags.map((t) => (
                      <Badge 
                        key={t.name} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-primary/20"
                        onClick={() => {
                          if (!listForm.tags.includes(t.name)) {
                            setListForm({ ...listForm, tags: [...listForm.tags, t.name] });
                          }
                        }}
                      >
                        {t.name} ({t.count})
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {listForm.list_type === "smart" && (
              <div className="space-y-4 p-4 border border-border/50 rounded-lg">
                <p className="text-sm font-medium">Filter Rules</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Contact Type</Label>
                    <Select
                      value={listForm.filter_rules.contact_type || ""}
                      onValueChange={(v) => setListForm({
                        ...listForm,
                        filter_rules: { ...listForm.filter_rules, contact_type: v || undefined }
                      })}
                    >
                      <SelectTrigger className="bg-muted/50">
                        <SelectValue placeholder="Any type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any type</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="vendor">Vendor</SelectItem>
                        <SelectItem value="partner">Partner</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">State</Label>
                    <Input
                      placeholder="e.g., CA"
                      className="bg-muted/50"
                      value={listForm.filter_rules.state || ""}
                      onChange={(e) => setListForm({
                        ...listForm,
                        filter_rules: { ...listForm.filter_rules, state: e.target.value || undefined }
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Company Contains</Label>
                    <Input
                      placeholder="e.g., Construction"
                      className="bg-muted/50"
                      value={listForm.filter_rules.company || ""}
                      onChange={(e) => setListForm({
                        ...listForm,
                        filter_rules: { ...listForm.filter_rules, company: e.target.value || undefined }
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Created After</Label>
                    <Input
                      type="date"
                      className="bg-muted/50"
                      value={listForm.filter_rules.created_after || ""}
                      onChange={(e) => setListForm({
                        ...listForm,
                        filter_rules: { ...listForm.filter_rules, created_after: e.target.value || undefined }
                      })}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setIsListDialogOpen(false); resetListForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateList} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingItem ? "Update" : "Create"} List
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Template Builder */}
      <EmailBuilder
        isOpen={isTemplateBuilderOpen}
        onClose={() => { setIsTemplateBuilderOpen(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSave={handleSaveTemplate}
      />

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={isCampaignDialogOpen} onOpenChange={setIsCampaignDialogOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="e.g., Q1 Newsletter"
                className="bg-muted/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                placeholder="Internal notes about this campaign"
                className="bg-muted/50"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Recipient List</Label>
              <Select
                value={campaignForm.list_id || "none"}
                onValueChange={(v) => setCampaignForm({ ...campaignForm, list_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="bg-muted/50">
                  <SelectValue placeholder="Select a list" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No list selected</SelectItem>
                  {lists.filter(l => l.status === "active").map((list) => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name} ({list.member_count} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email Template</Label>
              <Select
                value={campaignForm.template_id || "none"}
                onValueChange={(v) => setCampaignForm({ ...campaignForm, template_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="bg-muted/50">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template selected</SelectItem>
                  {templates.filter(t => t.status === "active").map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject Override</Label>
              <Input
                value={campaignForm.subject_override}
                onChange={(e) => setCampaignForm({ ...campaignForm, subject_override: e.target.value })}
                placeholder="Leave blank to use template subject"
                className="bg-muted/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Schedule Send (Optional)
              </Label>
              <Input
                type="datetime-local"
                value={campaignForm.scheduled_at}
                onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })}
                className="bg-muted/50"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to send immediately when you click Send
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setIsCampaignDialogOpen(false); resetCampaignForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateCampaign} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingItem ? "Update" : "Create"} Campaign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Analytics Dialog */}
      <Dialog open={!!selectedCampaign} onOpenChange={(open) => !open && setSelectedCampaign(null)}>
        <DialogContent className="bg-card max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Campaign Analytics: {selectedCampaign?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingAnalytics ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Loading campaign analytics...</p>
            </div>
          ) : campaignAnalytics ? (
            <div className="space-y-6 py-4">
              {/* Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Recipients</p>
                    <p className="text-2xl font-bold">{campaignAnalytics.stats.total_recipients.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Delivered</p>
                    <p className="text-2xl font-bold text-green-500">{campaignAnalytics.stats.total_delivered.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Opened</p>
                    <p className="text-2xl font-bold text-blue-500">{campaignAnalytics.stats.total_opened.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Clicked</p>
                    <p className="text-2xl font-bold text-purple-500">{campaignAnalytics.stats.total_clicked.toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Rate Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="p-4 text-center">
                    <MailOpen className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-3xl font-bold">{campaignAnalytics.rates.open_rate}%</p>
                    <p className="text-xs text-muted-foreground">Open Rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/30">
                  <CardContent className="p-4 text-center">
                    <MousePointerClick className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                    <p className="text-3xl font-bold">{campaignAnalytics.rates.click_rate}%</p>
                    <p className="text-xs text-muted-foreground">Click Rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="p-4 text-center">
                    <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-green-500" />
                    <p className="text-3xl font-bold">{campaignAnalytics.rates.click_to_open_rate}%</p>
                    <p className="text-xs text-muted-foreground">Click-to-Open</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-500/10 border-orange-500/30">
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="h-6 w-6 mx-auto mb-2 text-orange-500" />
                    <p className="text-3xl font-bold">{campaignAnalytics.rates.bounce_rate}%</p>
                    <p className="text-xs text-muted-foreground">Bounce Rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Top Links */}
              {campaignAnalytics.top_links && campaignAnalytics.top_links.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Top Clicked Links
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {campaignAnalytics.top_links.map((link, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" title={link.url}>
                              {link.url}
                            </p>
                          </div>
                          <div className="flex items-center gap-4 ml-4">
                            <div className="text-center">
                              <p className="text-lg font-bold">{link.clicks}</p>
                              <p className="text-xs text-muted-foreground">Clicks</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-primary">{link.unique_clicks}</p>
                              <p className="text-xs text-muted-foreground">Unique</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Hourly Distribution */}
              {campaignAnalytics.hourly_distribution && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Engagement by Hour
                    </CardTitle>
                    <CardDescription>Opens and clicks distribution</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-1 h-32 items-end">
                      {campaignAnalytics.hourly_distribution.map((h, idx) => {
                        const maxVal = Math.max(...campaignAnalytics.hourly_distribution.map(x => x.opened + x.clicked), 1);
                        const height = ((h.opened + h.clicked) / maxVal) * 100;
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center gap-1" title={`${h.hour}:00 - ${h.opened} opens, ${h.clicked} clicks`}>
                            <div 
                              className="w-full bg-primary/50 rounded-t transition-all hover:bg-primary"
                              style={{ height: `${Math.max(height, 2)}%` }}
                            />
                            <span className="text-[10px] text-muted-foreground">{h.hour}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">Hour of day (UTC)</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Unable to load analytics</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailMarketingPage;
